import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Card, 
  Typography, 
  Button, 
  Space, 
  Input, 
  Select, 
  Table, 
  Row, 
  Col,
  Tag,
  Spin,
  Popover,
  Checkbox,
  Radio
} from 'antd';
import { 
  SearchOutlined,
  PlusOutlined,
  DownloadOutlined,
  UploadOutlined,
  FilterOutlined,
  PlayCircleOutlined
} from '@ant-design/icons';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import AdminLayout from '@/components/Layouts/AdminLayout/AdminLayout';
import { adminSentenceApi } from '@/services/api';


const { Title } = Typography;
const { Search } = Input;
const DEFAULT_PAGE_SIZE = 10;

interface SentenceData {
  key: string;
  no: number;
  sentenceCode: string;
  sentence: string;
  ttsType: 'male' | 'female' | 'child';
  ttsGender: 'male' | 'female';
  ttsAge: 'child' | 'young' | 'adult' | 'elder';
  ttsSpeed: 'slow' | 'normal' | 'fast';
  ttsPitch: 'low' | 'medium' | 'high';
  isPublic: boolean;
  registrant: string;
  registrationDate: string;
}

interface TtsFilterState {
  gender: string[];
  age: string[];
  speed: string[];
  pitch: string[];
}

const AdminSentenceManagement: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const parseListParam = (value: string | null) =>
    value ? value.split(',').map((v) => v.trim()).filter(Boolean) : [];
  const initialKeyword = searchParams.get('q') || '';
  const initialPublic = searchParams.get('public') || '';
  const initialGender = parseListParam(searchParams.get('gender'));
  const initialAge = parseListParam(searchParams.get('age'));
  const initialSpeed = parseListParam(searchParams.get('speed'));
  const initialPitch = parseListParam(searchParams.get('pitch'));
  const initialPage = Number(searchParams.get('page')) || 1;
  const initialPageSize = Number(searchParams.get('size')) || DEFAULT_PAGE_SIZE;
  const [loading, setLoading] = useState(false);
  const [filteredSentences, setFilteredSentences] = useState<SentenceData[]>([]);
  const [searchText, setSearchText] = useState(initialKeyword);
  const [selectedPublicStatus, setSelectedPublicStatus] = useState<string>(initialPublic);
  const [ttsFilter, setTtsFilter] = useState<TtsFilterState>({
    gender: initialGender,
    age: initialAge,
    speed: initialSpeed,
    pitch: initialPitch
  });
  const [page, setPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [total, setTotal] = useState(0);
  const audioObjRef = useRef<HTMLAudioElement | null>(null);

  // 샘플 데이터 (필터 UI 참고용)
  const sampleSentences: SentenceData[] = [];

  // 필터 옵션들 (더미 데이터 - 실제로는 API에서 가져와야 함)
  const genderOptions = [
    { value: 'male', label: '남성' },
    { value: 'female', label: '여성' }
  ];

  const ageOptions = [
    { value: 'child', label: '아동' },
    { value: 'young', label: '청소년' },
    { value: 'adult', label: '성인' },
    { value: 'elder', label: '노년' }
  ];

  const speedOptions = [
    { value: 'slow', label: '느림' },
    { value: 'normal', label: '보통' },
    { value: 'fast', label: '빠름' }
  ];

  const pitchOptions = [
    { value: 'low', label: '낮음' },
    { value: 'medium', label: '보통' },
    { value: 'high', label: '높음' }
  ];

  const publicStatusOptions = [
    { value: 'true', label: '공개' },
    { value: 'false', label: '비공개' }
  ];

  const syncSearchParams = useCallback((nextPage: number, nextSize: number) => {
    const params = new URLSearchParams();
    if (searchText) params.set('q', searchText);
    if (selectedPublicStatus) params.set('public', selectedPublicStatus);
    if (ttsFilter.gender.length) params.set('gender', ttsFilter.gender.join(','));
    if (ttsFilter.age.length) params.set('age', ttsFilter.age.join(','));
    if (ttsFilter.speed.length) params.set('speed', ttsFilter.speed.join(','));
    if (ttsFilter.pitch.length) params.set('pitch', ttsFilter.pitch.join(','));
    if (nextPage > 1) params.set('page', String(nextPage));
    if (nextSize !== DEFAULT_PAGE_SIZE) params.set('size', String(nextSize));
    setSearchParams(params);
  }, [searchText, selectedPublicStatus, ttsFilter, setSearchParams]);

  const loadList = useCallback(async (pageIndex = 1, size = pageSize) => {
    setLoading(true);
    try {
      const params: any = {
        q: searchText,
        page: pageIndex - 1,
        size,
      };
      if (ttsFilter.gender.length === 1) params.gender = ttsFilter.gender[0];
      if (ttsFilter.age.length === 1) params.age = ttsFilter.age[0];
      if (ttsFilter.speed.length === 1) params.speed = ttsFilter.speed[0];
      if (ttsFilter.pitch.length === 1) params.pitch = ttsFilter.pitch[0];
      if (selectedPublicStatus !== '') params.isPublic = selectedPublicStatus === 'true';

      const res = await adminSentenceApi.list(params);
      if (res?.success) {
        const { content = [], totalElements = 0 } = res.data || {};
        const mapped: SentenceData[] = content.map((it: any, idx: number) => ({
          key: String(it.id),
          no: totalElements - ((pageIndex - 1) * size + idx),
          sentenceCode: it.code || `SEN-${String(it.id).padStart(4, '0')}`,
          sentence: it.text,
          ttsType: it.gender,
          ttsGender: it.gender,
          ttsAge: it.age,
          ttsSpeed: it.speed,
          ttsPitch: it.pitch,
          isPublic: it.public ?? it.isPublic,
          registrant: it.registrant || '-',
          registrationDate: (it.createdAt || '').split('T')[0] || ''
        }));
        setFilteredSentences(mapped);
        setTotal(totalElements);
        setPage(pageIndex);
        setPageSize(size);
        syncSearchParams(pageIndex, size);
      }
    } finally {
      setLoading(false);
    }
  }, [searchText, ttsFilter, selectedPublicStatus, pageSize, syncSearchParams]);

  useEffect(() => {
    loadList(page, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // TTS 필터 적용 함수 (서버 검색 호출로 대체)
  const applyTtsFilter = () => {
    loadList(1, pageSize);
  };

  // TTS 필터 초기화
  const resetTtsFilter = () => {
    setTtsFilter({
      gender: [],
      age: [],
      speed: [],
      pitch: []
    });
  };

  // 초기화 함수
  const handleReset = () => {
    setSearchText('');
    setSelectedPublicStatus('');
    resetTtsFilter();
    setPage(1);
    setPageSize(DEFAULT_PAGE_SIZE);
    loadList(1, DEFAULT_PAGE_SIZE);
  };

  // 선택된 필터 개수 계산
  const getSelectedFilterCount = () => {
    return ttsFilter.gender.length + ttsFilter.age.length + ttsFilter.speed.length + ttsFilter.pitch.length;
  };

  const handlePlay = async (record: SentenceData) => {
    try {
      const id = Number(record.key);
      if (!Number.isFinite(id)) return;
      const res: any = await adminSentenceApi.detail(id);
      const url = res?.data?.audioUrl || res?.audioUrl;
      if (!url) return;
      try { audioObjRef.current?.pause(); } catch {}
      const audio = new Audio();
      audio.src = url;
      audioObjRef.current = audio;
      await audio.play().catch(() => {});
    } catch {}
  };

  // 테이블 컬럼 정의
  const columns: ColumnsType<SentenceData> = [
    {
      title: '순번',
      dataIndex: 'no',
      key: 'no',
      width: 80,
      align: 'center'
    },
    {
      title: '문장코드',
      dataIndex: 'sentenceCode',
      key: 'sentenceCode',
      width: 120,
      align: 'center'
    },
    {
      title: '문장',
      dataIndex: 'sentence',
      key: 'sentence',
      ellipsis: true,
      render: (text: string) => (
        <div style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {text}
        </div>
      )
    },
    {
      title: 'TTS 유형',
      key: 'ttsType',
      width: 200,
      align: 'center',
      render: (_, record) => {
        const genderText = record.ttsGender === 'male' ? '남성' : '여성';
        const ageText = {
          child: '아동',
          young: '청소년', 
          adult: '성인',
          elder: '노년'
        }[record.ttsAge];
        const speedText = {
          slow: '느림',
          normal: '보통',
          fast: '빠름'
        }[record.ttsSpeed];
        const pitchText = {
          low: '낮음',
          medium: '보통',
          high: '높음'
        }[record.ttsPitch];
        
        return (
          <div style={{ fontSize: '12px', lineHeight: '1.4' }}>
            <div>{genderText} / {ageText}</div>
            <div>{speedText} / {pitchText}</div>
          </div>
        );
      }
    },
    {
      title: '공개상태',
      dataIndex: 'isPublic',
      key: 'isPublic',
      width: 100,
      align: 'center',
      render: (isPublic: boolean) => (
        <Tag color={isPublic ? '#52c41a' : '#ff4d4f'}>
          {isPublic ? '공개' : '비공개'}
        </Tag>
      )
    },
    {
      title: '등록자',
      dataIndex: 'registrant',
      key: 'registrant',
      width: 120,
      align: 'center'
    },
    {
      title: '등록일',
      dataIndex: 'registrationDate',
      key: 'registrationDate',
      width: 120,
      align: 'center'
    },
    {
      title: '미리듣기',
      key: 'preview',
      width: 120,
      align: 'center',
      render: (_, record) => (
        <Button icon={<PlayCircleOutlined />} onClick={(e) => { e.stopPropagation(); handlePlay(record); }}>
          재생
        </Button>
      )
    }
  ];

  // TTS 필터 팝오버 컨텐츠
  const ttsFilterContent = (
    <div style={{ minWidth: '400px' }}>
      <Row gutter={16}>
        <Col span={12}>
          <h4 style={{ marginBottom: 12, fontSize: '14px', fontWeight: 'bold' }}>성별</h4>
          <Checkbox.Group
            options={genderOptions}
            value={ttsFilter.gender}
            onChange={(checkedValues) => setTtsFilter(prev => ({ ...prev, gender: checkedValues as string[] }))}
          />
        </Col>
        <Col span={12}>
          <h4 style={{ marginBottom: 12, fontSize: '14px', fontWeight: 'bold' }}>연령대</h4>
          <Checkbox.Group
            options={ageOptions}
            value={ttsFilter.age}
            onChange={(checkedValues) => setTtsFilter(prev => ({ ...prev, age: checkedValues as string[] }))}
          />
        </Col>
      </Row>
      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col span={12}>
          <h4 style={{ marginBottom: 12, fontSize: '14px', fontWeight: 'bold' }}>속도</h4>
          <Checkbox.Group
            options={speedOptions}
            value={ttsFilter.speed}
            onChange={(checkedValues) => setTtsFilter(prev => ({ ...prev, speed: checkedValues as string[] }))}
          />
        </Col>
        <Col span={12}>
          <h4 style={{ marginBottom: 12, fontSize: '14px', fontWeight: 'bold' }}>피치</h4>
          <Checkbox.Group
            options={pitchOptions}
            value={ttsFilter.pitch}
            onChange={(checkedValues) => setTtsFilter(prev => ({ ...prev, pitch: checkedValues as string[] }))}
          />
        </Col>
      </Row>
      <div style={{ marginTop: 16, textAlign: 'right', borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
        <Space>
          <Button size="small" onClick={resetTtsFilter}>
            초기화
          </Button>
          <Button size="small" type="primary" onClick={applyTtsFilter}>
            적용
          </Button>
        </Space>
      </div>
    </div>
  );

  return (
    <AdminLayout>
      <div style={{ padding: '24px' }}>
        {/* 헤더 */}
        <div style={{ marginBottom: 24 }}>
          <Title level={2} style={{ marginBottom: 16 }}>문장 리스트</Title>
          
          {/* 검색 영역 */}
          <Row gutter={16} align="bottom" style={{ marginBottom: 16 }}>
            <Col span={8}>
              <Search
                placeholder="문장 검색"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onSearch={applyTtsFilter}
                size="large"
                allowClear
              />
            </Col>
            <Col span={2}>
              <Button 
                type="primary" 
                icon={<SearchOutlined />} 
                onClick={applyTtsFilter}
                size="large"
                style={{ width: '100%' }}
              >
                검색
              </Button>
            </Col>
            <Col span={2}>
              <Button 
                onClick={handleReset}
                size="large"
                style={{ width: '100%' }}
              >
                초기화
              </Button>
            </Col>
            <Col span={6}>
              <div>
                <div style={{ marginBottom: 8, fontSize: '14px', fontWeight: 'bold' }}>
                  TTS 유형 필터
                </div>
                <Popover
                  content={ttsFilterContent}
                  title="TTS 유형 필터"
                  trigger="click"
                  placement="bottomLeft"
                  arrow={{ pointAtCenter: false }}
                >
                  <Button 
                    icon={<FilterOutlined />} 
                    size="large"
                    style={{ width: '100%' }}
                  >
                    필터 선택
                    {getSelectedFilterCount() > 0 && (
                      <Tag color="#1890ff" style={{ marginLeft: 8 }}>
                        {getSelectedFilterCount()}
                      </Tag>
                    )}
                  </Button>
                </Popover>
              </div>
            </Col>
            <Col span={6}>
              <div>
                <div style={{ marginBottom: 8, fontSize: '14px', fontWeight: 'bold' }}>
                  공개 상태여부
                </div>
                <Radio.Group
                  value={selectedPublicStatus}
                  onChange={(e) => setSelectedPublicStatus(e.target.value)}
                  size="large"
                >
                  <Space>
                    <Radio.Button value="">전체</Radio.Button>
                    <Radio.Button value="true">공개</Radio.Button>
                    <Radio.Button value="false">비공개</Radio.Button>
                  </Space>
                </Radio.Group>
              </div>
            </Col>
          </Row>

          {/* 버튼 영역 */}
          <Row justify="end">
            <Space>
              <Button 
                type="primary" 
                icon={<PlusOutlined />} 
                size="large"
                onClick={() => navigate('/admin/sentence/create')}
              >
                새 문장 등록
              </Button>
            </Space>
          </Row>
        </div>

        {/* 테이블 */}
        <Table
          columns={columns}
          dataSource={filteredSentences}
          loading={loading}
          pagination={{
            current: page,
            total: total,
            pageSize: pageSize,
            showTotal: (total, range) => {
              if (!total) return `0-0 / 총 0개 문장`;
              const start = Math.max(total - range[1] + 1, 1);
              const end = Math.max(total - range[0] + 1, 1);
              return `${start}-${end} / 총 ${total}개 문장`;
            }
          }}
          scroll={{ x: 1000 }}
          size="middle"
          onRow={(record) => ({
            onClick: () => navigate(
              `/admin/sentence/detail/${record.key}`,
              { state: { from: `${location.pathname}${location.search}` } },
            ),
            style: { cursor: 'pointer' }
          })}
          onChange={(pagination) => {
            const nextPage = pagination.current || 1;
            const nextSize = pagination.pageSize || pageSize;
            loadList(nextPage, nextSize);
          }}
        />
      </div>
    </AdminLayout>
  );
};

export default AdminSentenceManagement; 